import { Component, OnInit, inject } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { GungNotificationService, gungAddRemoveSpinner } from 'gung-common';
import { first } from 'rxjs';
import { PunchoutAuditLogEntry } from '../../../models/punchout';
import { GungModalService } from '../../../services/gung-modal/gung-modal.service';
import { PunchoutAuditAdminService } from '../../../services/punchout/punchout-audit-admin.service';
import { punchoutIntakeStatusBadgeClass } from '../../../utils/punchout-audit-utils';

interface PayloadSection {
  labelKey: string;
  /** Rendered verbatim in a <pre> when raw, otherwise pretty-printed as JSON. */
  raw: boolean;
  value: unknown;
}

/**
 * Inspects one stored audit entry. Adapts to whichever payloads the entry carries: order requests
 * hold a raw cXML document, cart transfers hold original/converted maps.
 */
@Component({
  selector: 'lib-gung-settings-punchout-audit-detail-modal',
  templateUrl: './gung-settings-punchout-audit-detail-modal.component.html',
  styleUrl: './gung-settings-punchout-audit-detail-modal.component.scss',
  standalone: false
})
export class GungSettingsPunchoutAuditDetailModalComponent implements OnInit {
  protected auditService = inject(PunchoutAuditAdminService);
  protected modalService = inject(GungModalService);
  protected notificationService = inject(GungNotificationService);
  protected translateService = inject(TranslateService);
  public activeModal = inject(NgbActiveModal);

  /** Set by GungModalService before the modal renders. */
  entry!: PunchoutAuditLogEntry;
  configLabel?: string;
  /** Whether the backend can process order intake at all. See the gung-feature-edi gate. */
  ediEnabled = false;
  delegate?: GungModalService;
  componentId = GungSettingsPunchoutAuditDetailModalComponent.name;

  sections: PayloadSection[] = [];
  expanded: { [labelKey: string]: boolean } = {};
  reprocessing = false;

  /** Set once a reprocess succeeds, so the caller can update its row without re-fetching. */
  private updatedEntry?: PunchoutAuditLogEntry;

  ngOnInit(): void {
    this.sections = this.buildSections();
    // Open the first section by default. With one payload, that is the whole point of the modal.
    // With several payloads, the first one is the most relevant.
    const firstSection = this.sections[0];
    if (firstSection) {
      this.expanded[firstSection.labelKey] = true;
    }
  }

  get canReprocess(): boolean {
    return this.ediEnabled && this.entry?.type === 'ORDER_REQUEST';
  }

  get statusClass(): string {
    return punchoutIntakeStatusBadgeClass(this.entry?.status);
  }

  /** The browser sends the code at the top level and also in the metadata. */
  get failureCode(): string | undefined {
    return this.entry?.failureCode || this.entry?.metadata?.failureCode;
  }

  toggle(section: PayloadSection): void {
    this.expanded[section.labelKey] = !this.expanded[section.labelKey];
  }

  textOf(section: PayloadSection): string {
    return section.raw ? String(section.value ?? '') : JSON.stringify(section.value, undefined, 2);
  }

  copy(section: PayloadSection): void {
    navigator.clipboard.writeText(this.textOf(section)).then(() => {
      this.notificationService.notify('', this.translateService.instant('COPIED_TO_CLIPBOARD'), 'success');
    });
  }

  reprocess({ target }: { target: EventTarget | null }): void {
    if (!this.canReprocess || this.reprocessing) {
      return;
    }
    this.modalService
      .openConfirmYesNoModal('PUNCHOUT_REPROCESS', 'PUNCHOUT_REPROCESS_CONFIRM', { size: 'sm' })
      .then(ok => {
        if (!ok) {
          return;
        }
        gungAddRemoveSpinner(target as HTMLElement);
        this.reprocessing = true;
        this.auditService
          .process(this.entry.id)
          .pipe(first())
          .subscribe({
            next: updated => {
              this.entry = updated;
              this.updatedEntry = updated;
              this.sections = this.buildSections();
              this.reprocessing = false;
              gungAddRemoveSpinner(target as HTMLElement);
              this.notificationService.notify(
                '',
                this.translateService.instant('PUNCHOUT_REPROCESS_DONE', { value: updated.status }),
                updated.status === 'PROCESSED' ? 'success' : 'warning'
              );
            },
            error: error => {
              this.reprocessing = false;
              gungAddRemoveSpinner(target as HTMLElement);
              this.notificationService.notify(
                '',
                error?.error?.message || this.translateService.instant('ERROR'),
                'error'
              );
            }
          });
      })
      .catch(() => undefined);
  }

  close(): void {
    const ref = this.delegate?.modalRef.get(this.componentId);
    if (ref) {
      ref.close(this.updatedEntry);
    } else {
      this.activeModal.close(this.updatedEntry);
    }
  }

  private buildSections(): PayloadSection[] {
    const sections: PayloadSection[] = [];
    if (this.entry?.rawDocument) {
      sections.push({ labelKey: 'PUNCHOUT_RAW_DOCUMENT', raw: true, value: this.entry.rawDocument });
    }
    if (this.entry?.original) {
      sections.push({ labelKey: 'PUNCHOUT_ORIGINAL_PAYLOAD', raw: false, value: this.entry.original });
    }
    if (this.entry?.converted) {
      sections.push({ labelKey: 'PUNCHOUT_CONVERTED_PAYLOAD', raw: false, value: this.entry.converted });
    }
    if (this.entry?.setupCredentials) {
      sections.push({ labelKey: 'PUNCHOUT_SETUP_CREDENTIALS', raw: false, value: this.entry.setupCredentials });
    }
    if (this.entry?.extra && Object.keys(this.entry.extra).length > 0) {
      sections.push({ labelKey: 'EXTRA', raw: false, value: this.entry.extra });
    }
    return sections;
  }
}
